import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  BIOSDK_API_BASE_URL,
  CONVERTER_SINGLE_API_BASE,
  logBiosdkUiRoute,
} from './config';
import {
  EXTRACTION_MODE_MOSIP,
  EXTRACTION_MODE_PROCESSED,
  resolveFaceExtractUrl,
  resolveVariantStatus,
  resolveInputTypeLabel,
  EXTRACT_VARIANT_GUIDE_ROWS,
  CONTEXT_FACE_EXTRACT_MOSIP,
  CONTEXT_FACE_EXTRACT_PROCESSED,
  buildAcceptAttribute,
  formatListLabel,
  ISO_RECORD_LABEL,
  isPlainImageForContext,
  isIsoRecordInput,
  API_VARIANT_RAW,
  API_VARIANT_NIST,
  API_VARIANT_MINEX,
  MODALITY_FACE,
  PAGE_OP_EXTRACT,
  normalizeApiVariant,
  resolveNistVariantStatus,
  resolveMinexVariantStatus,
  MINEX_FACE_IRIS_BLOCKED_MESSAGE,
} from './biosdkRoutes';
import VariantBlockedBanner from './components/VariantBlockedBanner';
import { bdbToUint8Array, isBchqTemplate } from './bdbUtils';
import {
  NIST_FACE_SCENARIO_SINGLE,
  NIST_FACE_SCENARIO_TABLE5_FUSION,
  NIST_FACE_SCENARIO_TABLE6_MULTI_FACE,
  NIST_FACE_SCENARIOS,
} from './nistConstants';
import { extractNistFaceTemplates } from './nistFaceMatch';
import { buildNistFaceExtractSummary } from './nistExtractSummary';
import MultiFileUpload from './components/MultiFileUpload';
import NistScenarioSelector from './components/NistScenarioSelector';
import { User, Upload, Download, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import RoutePreviewChip from './components/RoutePreviewChip';
import InputTypeBadge from './components/InputTypeBadge';
import VariantStatusBanner from './components/VariantStatusBanner';
import RequestSummaryCard from './components/RequestSummaryCard';
import VariantTestingGuide from './components/VariantTestingGuide';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Label } from './components/ui/label';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from './components/ui/alert';

function FaceExtractor({ apiVariant = 'mosip' }) {
  const [file, setFile] = useState(null);
  const [extractionMode, setExtractionMode] = useState(EXTRACTION_MODE_MOSIP);
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [compression, setCompression] = useState('');
  const [isoImageUrl, setIsoImageUrl] = useState(null);
  const [jp2isoImageUrl, setjp2IsoImageUrl] = useState(null);
  const [jpegImageUrl, setJpegImageUrl] = useState(null);
  const [injpegImageUrl, setInJpegImageUrl] = useState(null);
  const [processedResult, setProcessedResult] = useState(null);
  const [requestSummary, setRequestSummary] = useState(null);
  const [timing, setTiming] = useState({
    convertInput: 0,
    convertJpeg: 0,
    convertIso: 0,
    extractionDurationMilliSeconds: 0,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [nistScenario, setNistScenario] = useState(NIST_FACE_SCENARIO_SINGLE);
  const [nistFiles, setNistFiles] = useState([]);
  const [nistExtractResults, setNistExtractResults] = useState([]);
  const [nistExtractSummary, setNistExtractSummary] = useState(null);

  const isNistMode = normalizeApiVariant(apiVariant) === API_VARIANT_NIST;
  const isMinexMode = normalizeApiVariant(apiVariant) === API_VARIANT_MINEX;
  const activeNistFiles = isNistMode ? nistFiles : file ? [file] : [];
  const fileExtension = activeNistFiles[0]
    ? activeNistFiles[0].name.split('.').pop().toLowerCase()
    : '';
  const isProcessedMode = isNistMode || extractionMode === EXTRACTION_MODE_PROCESSED;
  const extractContext = isProcessedMode ? CONTEXT_FACE_EXTRACT_PROCESSED : CONTEXT_FACE_EXTRACT_MOSIP;
  const effectiveExtractionMode = isNistMode ? EXTRACTION_MODE_PROCESSED : extractionMode;
  const routeResolution = useMemo(
    () => resolveFaceExtractUrl(BIOSDK_API_BASE_URL, effectiveExtractionMode, fileExtension, apiVariant),
    [effectiveExtractionMode, fileExtension, apiVariant]
  );
  const hasActiveInput = activeNistFiles.length > 0;
  const variantStatus = useMemo(() => {
    if (!hasActiveInput || isProcessedMode) return null;
    return resolveVariantStatus(apiVariant, [fileExtension], 'extract-template', BIOSDK_API_BASE_URL, CONTEXT_FACE_EXTRACT_MOSIP);
  }, [hasActiveInput, fileExtension, apiVariant, isProcessedMode]);
  const inputTypeLabel = useMemo(
    () => (hasActiveInput ? resolveInputTypeLabel([fileExtension], apiVariant, extractContext) : null),
    [hasActiveInput, fileExtension, apiVariant, extractContext]
  );
  const processedVariantStatus = useMemo(() => {
    if (!hasActiveInput || !isProcessedMode || routeResolution.blocked) return null;
    if (isNistMode) {
      return resolveNistVariantStatus(
        BIOSDK_API_BASE_URL,
        MODALITY_FACE,
        PAGE_OP_EXTRACT,
        [fileExtension],
        CONTEXT_FACE_EXTRACT_PROCESSED
      );
    }
    if (isMinexMode) {
      return resolveMinexVariantStatus(
        BIOSDK_API_BASE_URL,
        MODALITY_FACE,
        PAGE_OP_EXTRACT,
        [fileExtension],
        CONTEXT_FACE_EXTRACT_PROCESSED
      );
    }
    const ns = routeResolution.url?.includes('/iso/') ? 'iso' : 'raw';
    return {
      label: ns === 'iso' ? 'ISO variant' : 'Raw variant',
      isIsoVariant: ns === 'iso',
      isMosipVariant: false,
      isMosipBlocked: false,
      isNistVariant: false,
      endpointPath: routeResolution.url?.replace(BIOSDK_API_BASE_URL, '') || '',
    };
  }, [hasActiveInput, isProcessedMode, isNistMode, isMinexMode, routeResolution, fileExtension]);

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const toSignedByteJson = (arrayBuffer) => {
    const unsigned = new Uint8Array(arrayBuffer);
    const signed = Array.from(unsigned, (v) => (v > 127 ? v - 256 : v));
    return JSON.stringify(signed);
  };

  const resetOutput = () => {
    setIsoImageUrl(null);
    setJpegImageUrl(null);
    setProcessedResult(null);
    setNistExtractResults([]);
    setNistExtractSummary(null);
    setRequestSummary(null);
    setShowAdvanced(false);
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (!selectedFile) {
      setError('No file selected.');
      return;
    }

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    setInJpegImageUrl(null);
    let convertInputDuration = 0;

    try {
      if (isPlainImageForContext(ext, extractContext)) {
        setInJpegImageUrl(URL.createObjectURL(selectedFile));
      } else if (isIsoRecordInput(ext, extractContext)) {
        const isoFormData = new FormData();
        isoFormData.append('file', selectedFile);
        const startConvertInput = performance.now();
        const response = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        convertInputDuration = performance.now() - startConvertInput;
        const jpegBlob = new Blob([response.data], { type: 'image/jpeg' });
        setInJpegImageUrl(URL.createObjectURL(jpegBlob));
      } else if (ext === 'jp2' && !isProcessedMode) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const isoResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const isoBlob = new Blob([isoResponse.data], { type: 'application/octet-stream' });
        setjp2IsoImageUrl(URL.createObjectURL(isoBlob));
        const isoFormData = new FormData();
        isoFormData.append('file', isoBlob, 'converted-jp2.iso');
        const jpegResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        setInJpegImageUrl(URL.createObjectURL(new Blob([jpegResponse.data], { type: 'image/jpeg' })));
      } else {
        setError('Unsupported file type.');
        setFile(null);
        return;
      }

      setTiming((prev) => ({ ...prev, convertInput: convertInputDuration }));
      resetOutput();
      setError(null);
      setSubmitted(false);
    } catch (err) {
      setError('An error occurred while processing the file.');
    }
  };

  const buildRequestBody = async () => {
    let facebdbPlaceholder = [];
    let convertInputDuration = 0;

    if (isProcessedMode) {
      if (isPlainImageForContext(fileExtension, CONTEXT_FACE_EXTRACT_PROCESSED)) {
        facebdbPlaceholder = toSignedByteJson(await file.arrayBuffer());
      } else if (isIsoRecordInput(fileExtension, CONTEXT_FACE_EXTRACT_PROCESSED)) {
        facebdbPlaceholder = toSignedByteJson(await file.arrayBuffer());
      }
      const templateResponse = await fetch('/face_extract_processed_inner_request-template.json');
      if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);
      const updatedTemplate = (await templateResponse.text()).replace('facebdb', facebdbPlaceholder);
      return { innerTemplate: updatedTemplate, convertInputDuration };
    }

    let isoBlob1;
    if (fileExtension === 'jp2') {
      isoBlob1 = await fetch(jp2isoImageUrl).then((res) => res.arrayBuffer());
      facebdbPlaceholder = toSignedByteJson(isoBlob1);
    } else if (isIsoRecordInput(fileExtension, CONTEXT_FACE_EXTRACT_MOSIP)) {
      facebdbPlaceholder = toSignedByteJson(await file.arrayBuffer());
    } else {
      const formData = new FormData();
      formData.append('file', file);
      const startConvertInput = performance.now();
      const isoResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertImageFileToIso`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'arraybuffer',
      });
      convertInputDuration = performance.now() - startConvertInput;
      facebdbPlaceholder = toSignedByteJson(isoResponse.data);
    }

    const templateResponse = await fetch('/face_extract_inner_request-template.json');
    if (!templateResponse.ok) throw new Error(`Template fetch failed with status: ${templateResponse.status}`);
    const updatedTemplate = (await templateResponse.text())
      .replace('<width>', width)
      .replace('<height>', height)
      .replace('<compression_level>', compression)
      .replace('facebdb', facebdbPlaceholder);
    return { innerTemplate: updatedTemplate, convertInputDuration };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsProcessing(true);
    resetOutput();
    setError(null);

    const extractFiles = isNistMode ? nistFiles : file ? [file] : [];
    if (!extractFiles.length) {
      setError('Please upload a file.');
      setIsProcessing(false);
      return;
    }

    if (isNistMode && nistScenario === NIST_FACE_SCENARIO_TABLE5_FUSION && extractFiles.length < 2) {
      setError('Table 5 fusion requires at least 2 images.');
      setIsProcessing(false);
      return;
    }

    if (routeResolution.blocked) {
      setError(routeResolution.reason);
      setIsProcessing(false);
      return;
    }

    if (!isProcessedMode) {
      if (!width || !height || !compression) {
        setError('Please provide width, height, and compression for MOSIP extract.');
        setIsProcessing(false);
        return;
      }
      if (isNaN(width) || isNaN(height) || isNaN(compression) || width < 50 || height < 50 || compression < 0) {
        setError('Please provide valid numeric values for width, height, and compression.');
        setIsProcessing(false);
        return;
      }
    }

    try {
      if (isNistMode) {
        const startExtract = performance.now();
        const { templates: parsed, meta } = await extractNistFaceTemplates(BIOSDK_API_BASE_URL, extractFiles, nistScenario);
        const extractDuration = performance.now() - startExtract;
        const summary = buildNistFaceExtractSummary(nistScenario, meta, parsed.length, extractFiles.length);
        setNistExtractSummary(summary);
        const outputs = parsed.map((seg, idx) => ({
          index: idx,
          blobUrl: URL.createObjectURL(new Blob([seg.bdbBytes], { type: 'application/octet-stream' })),
          bdbBytes: seg.bdbBytes,
          level: seg.level,
          isBchq: isBchqTemplate(seg.bdbBytes),
        }));
        setNistExtractResults(outputs);
        setProcessedResult(outputs.length === 1 ? outputs[0] : null);
        setRequestSummary({
          variant: 'NIST variant',
          endpoint: '/raw/extract-processed-template',
          operation: 'extract-processed-template',
          inputs: extractFiles.map((f) => f.name).join(', '),
          extra: {
            Scenario: nistScenario,
            Summary: summary.description,
            'Input images': String(meta?.inputImageCount ?? extractFiles.length),
            'Templates extracted': String(parsed.length),
            ...(meta?.facesDetected != null ? { 'Faces detected': String(meta.facesDetected) } : {}),
            'Output format': 'BCHQ v1',
          },
        });
        setTiming({
          convertInput: timing.convertInput,
          convertJpeg: 0,
          convertIso: 0,
          extractionDurationMilliSeconds: extractDuration,
        });
        setShowAdvanced(true);
        setError(null);
        setIsProcessing(false);
        return;
      }

      const { innerTemplate, convertInputDuration } = await buildRequestBody();
      const versionRequestTemplateResponse = await fetch('/extract_request-template.json');
      if (!versionRequestTemplateResponse.ok) {
        throw new Error(`Version request template fetch failed with status: ${versionRequestTemplateResponse.status}`);
      }
      const versionRequestTemplateText = await versionRequestTemplateResponse.text();
      const base64EncodedExtract = arrayBufferToBase64(new TextEncoder().encode(innerTemplate));
      const finalRequestBodyExtract = versionRequestTemplateText.replace('encoded_request', base64EncodedExtract);

      const extractUrl = routeResolution.url;
      logBiosdkUiRoute('FaceExtractor', extractUrl.includes('/raw/') ? 'rawRoute' : 'isoRoute', extractUrl, {
        operation: routeResolution.operationPath,
        mode: extractionMode,
      });

      const extractResponse = await axios.post(extractUrl, finalRequestBodyExtract, {
        headers: { 'Content-Type': 'application/json' },
      });

      const extractResult = extractResponse.data?.response?.response?.segments?.[0];
      if (!extractResult) throw new Error('Failed to extract the BDB from the response');

      const bdbBytes = bdbToUint8Array(extractResult.bdb);
      const level = extractResult?.bdbInfo?.level || 'UNKNOWN';
      const others = extractResult?.others;
      const extractionDurationMilliSeconds = Number(others?.extractionDurationMilliSeconds) || 0;

      setRequestSummary({
        variant: isProcessedMode ? processedVariantStatus?.label : variantStatus?.label,
        isMosipVariant: isProcessedMode ? false : variantStatus?.isMosipVariant,
        mosipVariantYesNo: isProcessedMode ? false : variantStatus?.isMosipVariant,
        endpoint: routeResolution.url.replace(BIOSDK_API_BASE_URL, ''),
        inputType: inputTypeLabel?.label?.replace('Input: ', ''),
        operation: routeResolution.operationPath,
        inputs: file.name,
        extra: {
          Mode: isProcessedMode ? 'Processed template' : 'MOSIP extract',
          'bdbInfo.level': level,
          'Output format': isProcessedMode ? 'BCHQ v1' : 'ISO/IEC 19794-5',
          'BDB size': `${bdbBytes.length} bytes`,
        },
      });

      if (isProcessedMode) {
        const blobUrl = URL.createObjectURL(new Blob([bdbBytes], { type: 'application/octet-stream' }));
        setProcessedResult({
          blobUrl,
          bdbBytes,
          level,
          isBchq: isBchqTemplate(bdbBytes),
        });
        setTiming({
          convertInput: timing.convertInput,
          convertJpeg: convertInputDuration,
          convertIso: 0,
          extractionDurationMilliSeconds,
        });
      } else {
        const isoBlob = new Blob([bdbBytes], { type: 'application/octet-stream' });
        const isoUrl = URL.createObjectURL(isoBlob);
        setIsoImageUrl(isoUrl);

        const isoFormData1 = new FormData();
        isoFormData1.append('file', isoBlob, 'extracted-image.iso');
        const startConvertOutput = performance.now();
        const outputResponse = await axios.post(`${CONVERTER_SINGLE_API_BASE}/convertFromIsoFile`, isoFormData1, {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'arraybuffer',
        });
        const convertOutputDuration = performance.now() - startConvertOutput;
        setJpegImageUrl(URL.createObjectURL(new Blob([outputResponse.data], { type: 'image/jpeg' })));
        setTiming({
          convertInput: timing.convertInput,
          convertJpeg: convertInputDuration,
          convertIso: convertOutputDuration,
          extractionDurationMilliSeconds,
        });
      }

      setShowAdvanced(true);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || 'An error occurred during the extraction process';
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadIso = () => {
    if (isoImageUrl) {
      const link = document.createElement('a');
      link.href = isoImageUrl;
      link.download = 'extracted-image.iso';
      link.click();
    }
  };

  const handleDownloadProcessed = () => {
    if (processedResult?.blobUrl) {
      const link = document.createElement('a');
      link.href = processedResult.blobUrl;
      link.download = 'face-template.bchq';
      link.click();
    }
  };

  const handleReset = () => {
    setFile(null);
    setNistFiles([]);
    setNistScenario(NIST_FACE_SCENARIO_SINGLE);
    setWidth('');
    setHeight('');
    setCompression('');
    setInJpegImageUrl(null);
    resetOutput();
    setError(null);
    setSubmitted(false);
  };

  const hasResult = Boolean(isoImageUrl || processedResult || nistExtractResults.length);
  const submitDisabled =
    isMinexMode ||
    processedVariantStatus?.isMinexBlocked ||
    (isNistMode ? !nistFiles.length : !file) ||
    isProcessing ||
    routeResolution.blocked ||
    (!isProcessedMode && !isNistMode && (!width || !height || !compression));

  return (
    <div className="biochq-page">
      <div className="biochq-container">
        <Card className="biochq-card face">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <User className="h-8 w-8 text-face" />
              <CardTitle className="text-3xl font-bold text-gray-800">Face Extractor</CardTitle>
            </div>
            <CardDescription className="text-gray-600">
              {isNistMode
                ? 'NIST / FRVT face extract · BCHQ processed template'
                : 'Supports MOSIP, ISO, Raw, and NIST namespace variants'}
            </CardDescription>
            {!isMinexMode && (
              <p className="text-center text-sm mt-2">
                <Link to="/extractor/face/fusion-lab" className="text-blue-600 hover:underline">
                  Open fusion lab (/face/extract-fused-template &amp; group faces)
                </Link>
              </p>
            )}
            {!isNistMode && (
              <div className="flex justify-center gap-2 mt-4">
                <Button
                  type="button"
                  variant={extractionMode === EXTRACTION_MODE_MOSIP ? 'face' : 'outline'}
                  onClick={() => { setExtractionMode(EXTRACTION_MODE_MOSIP); resetOutput(); setError(null); }}
                >
                  MOSIP extract
                </Button>
                <Button
                  type="button"
                  variant={extractionMode === EXTRACTION_MODE_PROCESSED ? 'face' : 'outline'}
                  onClick={() => { setExtractionMode(EXTRACTION_MODE_PROCESSED); resetOutput(); setError(null); }}
                >
                  Processed template
                </Button>
              </div>
            )}
            <div className="flex justify-center mt-2">
              <Badge variant={isProcessedMode ? 'secondary' : 'face'}>
                {isNistMode
                  ? 'NIST · BCHQ template'
                  : isProcessedMode
                    ? 'Processed · BCHQ template'
                    : 'MOSIP · resize / ISO segment'}
              </Badge>
            </div>
          </CardHeader>

          <CardContent>
            <VariantBlockedBanner
              apiVariant={apiVariant}
              message={isMinexMode ? MINEX_FACE_IRIS_BLOCKED_MESSAGE : null}
            />
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                {isNistMode && (
                  <NistScenarioSelector
                    label="NIST extract scenario"
                    scenarios={NIST_FACE_SCENARIOS}
                    value={nistScenario}
                    onChange={(v) => {
                      setNistScenario(v);
                      setNistFiles([]);
                      setFile(null);
                      setInJpegImageUrl(null);
                      resetOutput();
                      setError(null);
                    }}
                  />
                )}

                <div className="space-y-2">
                  <Label htmlFor="file" className="text-gray-700 font-medium">
                    {isNistMode && nistScenario === NIST_FACE_SCENARIO_TABLE5_FUSION
                      ? 'Upload K images (same person)'
                      : 'Upload Face Image'}
                  </Label>
                  {isNistMode ? (
                    <MultiFileUpload
                      files={nistFiles}
                      onChange={(next) => {
                        setNistFiles(next);
                        setFile(next[0] || null);
                        setInJpegImageUrl(
                          next[0] && next[0].type?.startsWith('image/')
                            ? URL.createObjectURL(next[0])
                            : null
                        );
                        resetOutput();
                        setError(null);
                      }}
                      accept={buildAcceptAttribute(apiVariant, extractContext)}
                      multiple={nistScenario !== NIST_FACE_SCENARIO_TABLE6_MULTI_FACE}
                      maxFiles={nistScenario === NIST_FACE_SCENARIO_TABLE6_MULTI_FACE ? 1 : undefined}
                      hint={`Supported: ${formatListLabel(extractContext)} → /raw/extract-processed-template`}
                    />
                  ) : (
                    <div className="upload-zone">
                      <input
                        type="file"
                        id="file"
                        accept={buildAcceptAttribute(apiVariant, extractContext)}
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label htmlFor="file" className="cursor-pointer">
                        <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
                        {normalizeApiVariant(apiVariant) === API_VARIANT_RAW ? (
                          <p className="text-xs text-gray-400 mt-1">
                            Supported: {formatListLabel(extractContext)} → /raw/…
                          </p>
                        ) : (
                          <>
                            <p className="text-xs text-gray-400 mt-1">
                              Plain image: {formatListLabel(extractContext)} → /raw/…
                            </p>
                            <p className="text-xs text-gray-400">{ISO_RECORD_LABEL}</p>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>

                {(file || nistFiles.length > 0) && injpegImageUrl && (
                  <div className="space-y-2">
                    <Label className="text-gray-700 font-medium">Preview</Label>
                    <div className="preview-container">
                      <img src={injpegImageUrl} alt="Uploaded face" className="preview-image" />
                      <Badge variant="face" className="absolute top-2 right-2">Face</Badge>
                    </div>
                    <p className="text-xs text-gray-500 text-center">{file.name}</p>
                  </div>
                )}

                {hasActiveInput && inputTypeLabel && (
                  <InputTypeBadge fileExtensions={[fileExtension]} apiVariant={apiVariant} contextKey={extractContext} />
                )}

                {hasActiveInput && isProcessedMode && (
                  <VariantStatusBanner variantStatus={processedVariantStatus} />
                )}
                {hasActiveInput && !isProcessedMode && <VariantStatusBanner variantStatus={variantStatus} />}

                {hasActiveInput && routeResolution.url && <RoutePreviewChip url={routeResolution.url} />}

                {hasActiveInput && routeResolution.blocked && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Route blocked</AlertTitle>
                    <AlertDescription>{routeResolution.reason}</AlertDescription>
                  </Alert>
                )}

                {!isProcessedMode ? (
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="width" className="text-gray-700 font-medium text-sm">Width (px)</Label>
                      <Input type="number" id="width" placeholder="Width" value={width} onChange={(e) => setWidth(e.target.value)} min="50" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="height" className="text-gray-700 font-medium text-sm">Height (px)</Label>
                      <Input type="number" id="height" placeholder="Height" value={height} onChange={(e) => setHeight(e.target.value)} min="50" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="compression" className="text-gray-700 font-medium text-sm">Compression</Label>
                      <Input type="number" id="compression" placeholder="Level" value={compression} onChange={(e) => setCompression(e.target.value)} min="0" />
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Width, height, and compression are not used for processed template extract.</p>
                )}

                <div className="flex gap-3">
                  <Button onClick={handleSubmit} disabled={submitDisabled} variant="face" className="flex-1">
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <User className="mr-2 h-4 w-4" />
                        Extract Template
                      </>
                    )}
                  </Button>
                  <Button onClick={handleReset} variant="outline" disabled={isProcessing}>Reset</Button>
                </div>

                <VariantTestingGuide
                  title="Variant testing guide (Extract)"
                  rows={isProcessedMode ? [
                    { variant: 'Raw', input: 'JPG/PNG/BMP', header: 'either', endpoint: '/raw/extract-processed-template', highlight: false },
                    { variant: 'ISO', input: '.iso', header: 'iso', endpoint: '/iso/extract-processed-template', highlight: false },
                  ] : EXTRACT_VARIANT_GUIDE_ROWS}
                />
              </div>

              <div className="space-y-6">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                {requestSummary && !error && <RequestSummaryCard summary={requestSummary} />}

                {isoImageUrl && !error && (
                  <>
                    <Alert variant="success">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertTitle>Extraction Complete</AlertTitle>
                      <AlertDescription>MOSIP face segment extracted successfully</AlertDescription>
                    </Alert>
                    {jpegImageUrl && (
                      <div className="space-y-2">
                        <Label className="text-gray-700 font-medium">Extracted Result</Label>
                        <div className="preview-container">
                          <img src={jpegImageUrl} alt="Extracted face" className="preview-image" />
                        </div>
                      </div>
                    )}
                    <Button onClick={handleDownloadIso} variant="outline" className="w-full">
                      <Download className="mr-2 h-4 w-4" />
                      Download ISO Template
                    </Button>
                  </>
                )}

                {(processedResult || nistExtractResults.length > 0) && !error && (
                  <>
                    <Alert variant="success">
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertTitle>{nistExtractSummary?.title || 'Extraction Complete'}</AlertTitle>
                      <AlertDescription>
                        {nistExtractSummary?.description ||
                          (nistExtractResults.length > 1
                            ? `${nistExtractResults.length} BCHQ templates extracted`
                            : 'Processed BCHQ template extracted successfully')}
                        {nistExtractSummary?.detail && (
                          <p className="text-sm mt-2 opacity-90">{nistExtractSummary.detail}</p>
                        )}
                      </AlertDescription>
                    </Alert>
                    {(nistExtractResults.length ? nistExtractResults : [processedResult]).map((result, idx) => (
                      <Card key={`nist-tpl-${idx}`} className="bg-gray-50">
                        <CardContent className="pt-6 space-y-2 text-sm">
                          {nistExtractResults.length > 1 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Face index</span>
                              <span className="font-medium">{idx + 1} of {nistExtractResults.length}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-600">BCHQ magic</span>
                            <Badge variant={result.isBchq ? 'success' : 'destructive'}>
                              {result.isBchq ? 'Valid' : 'Missing'}
                            </Badge>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Template size</span>
                            <span className="font-medium">{result.bdbBytes.length} bytes</span>
                          </div>
                          <Button
                            onClick={() => {
                              const a = document.createElement('a');
                              a.href = result.blobUrl;
                              a.download = `nist-face-template-${idx}.bin`;
                              a.click();
                            }}
                            variant="outline"
                            className="w-full mt-2"
                          >
                            <Download className="mr-2 h-4 w-4" />
                            Download template{nistExtractResults.length > 1 ? ` ${idx + 1}` : ''}
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </>
                )}

                {showAdvanced && hasResult && (
                  <Card className="timing-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Timing Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">Display Conversion</span><span className="font-medium">{timing.convertInput.toFixed(2)} ms</span></div>
                      {!isProcessedMode && (
                        <>
                          <div className="flex justify-between"><span className="text-gray-600">ISO Conversion</span><span className="font-medium">{timing.convertJpeg.toFixed(2)} ms</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Output Conversion</span><span className="font-medium">{timing.convertIso.toFixed(2)} ms</span></div>
                        </>
                      )}
                      <div className="flex justify-between"><span className="text-gray-600">Extraction Time</span><span className="font-medium text-primary">{timing.extractionDurationMilliSeconds.toFixed(2)} ms</span></div>
                    </CardContent>
                  </Card>
                )}

                {!hasResult && !error && submitted && (
                  <div className="flex items-center justify-center h-48 text-gray-400">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                )}

                {!hasResult && !error && !submitted && (
                  <div className="empty-state">
                    <User className="h-12 w-12 mb-2 opacity-50" />
                    <p className="text-sm">Upload a face image and click Extract</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default FaceExtractor;
